import { useEffect, useState } from "react";

// Shared "extend to viewport bottom" state for the chat-with-the-test
// pages (Review and Revise Items, Edit Test). False (default) shows the
// assistant at its compact natural height; true stretches it down to
// the bottom of the viewport from its sticky top. Module-level so the
// preference survives page navigations, and persisted to localStorage
// so a refresh / new test keeps the same setting.
//
// Default: false (= compact / "short"). New users start with the
// shorter assistant; extending is opt-in.

const STORAGE_KEY = "testcraft.chat.extended";
const DEFAULT_VALUE = false;

function readInitial(): boolean {
  try {
    if (typeof window === "undefined") return DEFAULT_VALUE;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
    return DEFAULT_VALUE;
  } catch {
    return DEFAULT_VALUE;
  }
}

let sharedExtended = readInitial();
const listeners = new Set<(value: boolean) => void>();

function setShared(next: boolean) {
  sharedExtended = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
  } catch {
    /* private mode */
  }
  listeners.forEach((listener) => listener(next));
}

/** Reset to the default (false / compact). Called from
 *  TestContext.resetTest() so a new test starts in the short state. */
export function resetChatExtended() {
  setShared(DEFAULT_VALUE);
}

export function useChatExtended(): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState(sharedExtended);

  useEffect(() => {
    listeners.add(setValue);
    setValue(sharedExtended);
    return () => {
      listeners.delete(setValue);
    };
  }, []);

  return [value, setShared];
}
